import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'aihc_token';

const client = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Attach token on every request
client.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally
client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('aihc_user');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const setToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};

export const getToken = () => localStorage.getItem(TOKEN_KEY);

// ==================== AUTH ====================
export const apiLogin = async (email, password, role) => {
  const payload = { email, password };
  if (role) payload.role = role;
  const { data } = await client.post('/auth/login', payload);
  return data;
};

export const apiMe = async () => {
  const { data } = await client.get('/auth/me');
  return data;
};

export const apiSetLanguage = async (language) => {
  const { data } = await client.put('/auth/language', { language });
  return data;
};

// ==================== CHAT ====================
export const apiSendChat = async ({ content, category, chat_id }) => {
  const { data } = await client.post('/chat', { content, category, chat_id });
  return data;
};

export const apiListChats = async () => {
  const { data } = await client.get('/chats');
  return data;
};

export const apiGetChat = async (id) => {
  const { data } = await client.get(`/chats/${id}`);
  return data;
};

export const apiDeleteChat = async (id) => {
  const { data } = await client.delete(`/chats/${id}`);
  return data;
};

// ==================== VACATION ====================
export const apiCreateVacation = async (payload) => {
  const { data } = await client.post('/vacation-requests', payload);
  return data;
};

export const apiListVacations = async () => {
  const { data } = await client.get('/vacation-requests');
  return data;
};

export const apiReviewVacation = async (id, payload) => {
  const { data } = await client.patch(`/vacation-requests/${id}`, payload);
  return data;
};

// ==================== SICKNESS ====================
export const apiCreateSickness = async (payload) => {
  const { data } = await client.post('/sickness-protocols', payload);
  return data;
};

export const apiListSickness = async () => {
  const { data } = await client.get('/sickness-protocols');
  return data;
};

// ==================== WORKPLACES ====================
export const apiListWorkplaces = async () => {
  const { data } = await client.get('/workplaces');
  return data;
};

export const apiCreateWorkplace = async (payload) => {
  const { data } = await client.post('/workplaces', payload);
  return data;
};

export const apiUpdateWorkplace = async (id, payload) => {
  const { data } = await client.put(`/workplaces/${id}`, payload);
  return data;
};

export const apiDeleteWorkplace = async (id) => {
  const { data } = await client.delete(`/workplaces/${id}`);
  return data;
};

// ==================== ATTENDANCE ====================
export const apiClockIn = async ({ type, lat, lng }) => {
  const { data } = await client.post('/attendance', { type, lat, lng });
  return data;
};

export const apiListAttendance = async (params = {}) => {
  const { data } = await client.get('/attendance', { params });
  return data;
};

// ==================== EMPLOYEES (HR) ====================
export const apiListEmployees = async () => {
  const { data } = await client.get('/hr/employees');
  return data;
};

export const apiCreateEmployee = async (payload) => {
  const { data } = await client.post('/hr/employees', payload);
  return data;
};

export const apiUpdateEmployee = async (id, payload) => {
  const { data } = await client.put(`/hr/employees/${id}`, payload);
  return data;
};

export const apiToggleEmployeeStatus = async (id, active) => {
  const { data } = await client.patch(`/hr/employees/${id}/status`, { active });
  return data;
};

export const apiDeleteEmployee = async (id) => {
  const { data } = await client.delete(`/hr/employees/${id}`);
  return data;
};

export const apiResetPassword = async (id, temp_password) => {
  const { data } = await client.post(`/hr/employees/${id}/reset-password`, { temp_password });
  return data;
};

export const apiChangePassword = async (current_password, new_password) => {
  const { data } = await client.post('/auth/change-password', { current_password, new_password });
  return data;
};

// ==================== PAYSLIPS ====================
export const apiUploadPayslip = async (payload) => {
  const { data } = await client.post('/payslips', payload);
  return data;
};

export const apiListPayslips = async (employee_id) => {
  const params = employee_id ? { employee_id } : {};
  const { data } = await client.get('/payslips', { params });
  return data;
};

export const apiDownloadPayslip = async (id) => {
  const { data } = await client.get(`/payslips/${id}/download`);
  return data;
};

export const apiDeletePayslip = async (id) => {
  const { data } = await client.delete(`/payslips/${id}`);
  return data;
};

// ==================== ANNOUNCEMENTS ====================
export const apiCreateAnnouncement = async (payload) => {
  const { data } = await client.post('/announcements', payload);
  return data;
};

export const apiListAnnouncements = async () => {
  const { data } = await client.get('/announcements');
  return data;
};

export const apiMarkAnnouncementRead = async (id) => {
  const { data } = await client.post(`/announcements/${id}/read`);
  return data;
};

export const apiDeleteAnnouncement = async (id) => {
  const { data } = await client.delete(`/announcements/${id}`);
  return data;
};

// ==================== PRODUCTION MODULE ====================
// Customers
export const apiListCustomers = async () => (await client.get('/prod/customers')).data;
export const apiCreateCustomer = async (p) => (await client.post('/prod/customers', p)).data;
export const apiUpdateCustomer = async (id, p) => (await client.put(`/prod/customers/${id}`, p)).data;
export const apiDeleteCustomer = async (id) => (await client.delete(`/prod/customers/${id}`)).data;
export const apiCustomerDetail = async (id, period = 'all') => (await client.get(`/prod/customers/${id}/detail`, { params: { period } })).data;

// Operators
export const apiListOperators = async () => (await client.get('/prod/operators')).data;
export const apiCreateOperator = async (p) => (await client.post('/prod/operators', p)).data;
export const apiUpdateOperator = async (id, p) => (await client.put(`/prod/operators/${id}`, p)).data;
export const apiDeleteOperator = async (id) => (await client.delete(`/prod/operators/${id}`)).data;
export const apiOperatorHistory = async (id) => (await client.get(`/prod/operators/${id}/history`)).data;

// Work types (scoped per customer)
export const apiListWorkTypes = async (customer_id) => {
  const { data } = await client.get('/prod/work-types', { params: customer_id ? { customer_id } : {} });
  return data;
};
export const apiCreateWorkType = async (p) => (await client.post('/prod/work-types', p)).data;
export const apiUpdateWorkType = async (id, p) => (await client.put(`/prod/work-types/${id}`, p)).data;
export const apiDeleteWorkType = async (id) => (await client.delete(`/prod/work-types/${id}`)).data;

// Production entries (multi-operator)
export const apiCreateEntry = async (p) => (await client.post('/prod/entries', p)).data;
export const apiListEntries = async (params = {}) => (await client.get('/prod/entries', { params })).data;
export const apiUpdateEntry = async (id, p) => (await client.put(`/prod/entries/${id}`, p)).data;
export const apiDeleteEntry = async (id) => (await client.delete(`/prod/entries/${id}`)).data;

// Dashboard / Analytics
export const apiProdDashboard = async (period = 'month', customer_id) => {
  const params = { period };
  if (customer_id) params.customer_id = customer_id;
  return (await client.get('/prod/dashboard', { params })).data;
};
export const apiProdAlerts = async (min_streak = 3) => (await client.get('/prod/alerts', { params: { min_streak } })).data;
export const apiProdComparison = async (params = {}) => (await client.get('/prod/comparison', { params })).data;

// Export (CSV / XLSX)
export const apiProdExport = async ({ fmt = 'csv', customer_id, operator_id, date_from, date_to } = {}) => {
  const params = { fmt };
  if (customer_id) params.customer_id = customer_id;
  if (operator_id) params.operator_id = operator_id;
  if (date_from) params.date_from = date_from;
  if (date_to) params.date_to = date_to;
  const resp = await client.get('/prod/export', { params, responseType: 'blob' });
  return resp;
};

// Notification settings (HR)
export const apiGetNotificationSettings = async () => (await client.get('/hr/notification-settings')).data;
export const apiUpdateNotificationSettings = async (settings) => (await client.put('/hr/notification-settings', { settings })).data;
export const apiSendTestNotification = async (test_email) => (await client.post('/hr/notification-test', test_email ? { test_email } : {})).data;

export default client;
